// Optionale Haus-Ebene einer Liegenschaft (objekt_haeuser): Gruppierung der
// Einheiten, Anzeige-Adresse und Namensvorschlag. Reine Funktionen — die
// Datenbank-Zugriffe liegen in den Komponenten.
//
// Grundregel: Hat eine Liegenschaft keine Häuser, muss sich nichts ändern.
// gruppiereEinheitenNachHaus liefert dann genau eine Gruppe ohne Haus, und die
// Aufrufer zeigen sie ohne Überschrift an — wie vor der Haus-Ebene.

export interface Haus {
  id: string;
  objekt_id?: string;
  bezeichnung: string;
  strasse?: string | null;
  hausnummer?: string | null;
  plz?: string | null;
  stadt?: string | null;
  sortierung?: number | null;
  notizen?: string | null;
}

const text = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');

/** Anschrift eines Hauses; leere Teile fallen weg (nie „, " allein). */
export function hausAdresse(haus: Pick<Haus, 'strasse' | 'hausnummer' | 'plz' | 'stadt'>): string {
  const strasse = `${text(haus.strasse)} ${text(haus.hausnummer)}`.trim();
  const ort = `${text(haus.plz)} ${text(haus.stadt)}`.trim();
  return [strasse, ort].filter(Boolean).join(', ');
}

/** Häuser in Anzeige-Reihenfolge: Sortierung, dann Bezeichnung (natürlich, „Haus 2" vor „Haus 10"). */
export function sortiereHaeuser<T extends Haus>(haeuser: T[]): T[] {
  return [...haeuser].sort((a, b) => {
    const sa = a.sortierung ?? 0;
    const sb = b.sortierung ?? 0;
    if (sa !== sb) return sa - sb;
    return a.bezeichnung.localeCompare(b.bezeichnung, 'de', { numeric: true });
  });
}

/** Nächster freier Vorschlag: „Haus 1", „Haus 2", … — überspringt vergebene Nummern. */
export function naechsteHausBezeichnung(haeuser: Pick<Haus, 'bezeichnung'>[]): string {
  const vergeben = new Set(
    haeuser
      .map((h) => /^haus\s*(\d+)$/i.exec(h.bezeichnung.trim())?.[1])
      .filter(Boolean)
      .map((n) => Number(n)),
  );
  let n = 1;
  while (vergeben.has(n)) n += 1;
  return `Haus ${n}`;
}

export interface HausGruppe<E> {
  /** null = „Ohne Haus" bzw. Liegenschaft ohne Haus-Ebene. */
  haus: Haus | null;
  einheiten: E[];
}

/**
 * Gruppiert Einheiten nach Haus. Reihenfolge: Häuser wie sortiereHaeuser,
 * danach „Ohne Haus". Häuser ohne Einheiten bleiben als leere Gruppe sichtbar
 * (sonst sähe ein frisch angelegtes Haus aus, als gäbe es es nicht);
 * „Ohne Haus" erscheint nur, wenn dort etwas liegt.
 *
 * Ohne Häuser gibt es genau eine Gruppe {haus: null} mit allen Einheiten —
 * auch dann, wenn sie leer ist, damit Aufrufer immer dieselbe Form bekommen.
 */
export function gruppiereEinheitenNachHaus<E>(
  einheiten: E[],
  haeuser: Haus[],
  hausIdVon: (e: E) => string | null | undefined,
): HausGruppe<E>[] {
  if (haeuser.length === 0) return [{ haus: null, einheiten }];

  const gueltig = new Set(haeuser.map((h) => h.id));
  const nachHaus = new Map<string, E[]>();
  const ohneHaus: E[] = [];

  einheiten.forEach((e) => {
    const id = hausIdVon(e);
    // Unbekannte haus_id (z. B. gerade gelöschtes Haus) landet bei „Ohne Haus“,
    // statt die Einheit aus der Liste fallen zu lassen.
    if (id && gueltig.has(id)) {
      const liste = nachHaus.get(id);
      if (liste) liste.push(e);
      else nachHaus.set(id, [e]);
    } else {
      ohneHaus.push(e);
    }
  });

  const gruppen: HausGruppe<E>[] = sortiereHaeuser(haeuser).map((haus) => ({
    haus,
    einheiten: nachHaus.get(haus.id) || [],
  }));
  if (ohneHaus.length > 0) gruppen.push({ haus: null, einheiten: ohneHaus });
  return gruppen;
}
